import { Home, ShoppingBag, Heart, User } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { useCart } from "@/context/CartContext";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { CartView } from "./CartView";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export function BottomNav() {
  const { totalItems } = useCart();
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  // Auth state
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [isRegistering, setIsRegistering] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    setAuthLoading(true);
    
    // Simplification: Using email as phone format for Supabase Auth if needed, 
    // or just standard phone if configured. 
    // To keep it simple for the user, we'll use a virtual email format: phone@atelie.com
    const email = `${phone.replace(/\D/g, '')}@atelie.com`;

    try {
      if (isRegistering) {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { phone }
          }
        });
        if (error) throw error;
        toast.success("Conta criada com sucesso!");
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
        toast.success("Bem-vindo(a) de volta!");
      }
      setIsProfileOpen(false);
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    toast.success("Sessão encerrada");
  };

  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t bg-white/80 backdrop-blur-lg px-6 py-3 pb-6 md:hidden">
      <div className="mx-auto flex max-w-md items-center justify-between">
         <Link to="/" className="flex flex-col items-center gap-1 text-pink-500">
           <Home className="h-6 w-6" />
           <span className="text-[10px] font-medium uppercase tracking-wider">Início</span>
         </Link>
        
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetTrigger asChild>
            <button id="cart-trigger" className="relative flex flex-col items-center gap-1 text-gray-400 hover:text-pink-500 transition-colors">
              <ShoppingBag className="h-6 w-6" />
              {totalItems > 0 && (
                <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-pink-500 text-[10px] font-bold text-white">
                  {totalItems}
                </span>
              )}
              <span className="text-[10px] font-medium uppercase tracking-wider">Encomenda</span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[85vh] rounded-t-[32px] p-0 border-none flex flex-col overflow-hidden">
            <div className="flex-shrink-0">
              <div className="h-1.5 w-12 bg-gray-200 rounded-full mx-auto mt-3 mb-2" />
              <SheetHeader className="px-6 py-2">
                <SheetTitle className="text-2xl font-serif text-gray-900">Minha Encomenda</SheetTitle>
              </SheetHeader>
            </div>
            <div className="flex-1 overflow-hidden">
              <CartView />
            </div>
          </SheetContent>
        </Sheet>

         <button className="flex flex-col items-center gap-1 text-gray-400 hover:text-pink-500 transition-colors">
           <Heart className="h-6 w-6" />
           <span className="text-[10px] font-medium uppercase tracking-wider">Favoritos</span>
         </button>
         
         <Sheet open={isProfileOpen} onOpenChange={setIsProfileOpen}>
          <SheetTrigger asChild>
            <button className="flex flex-col items-center gap-1 text-gray-400 hover:text-pink-500 transition-colors">
              <User className="h-6 w-6" />
              <span className="text-[10px] font-medium uppercase tracking-wider">Perfil</span>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="h-[60vh] rounded-t-[32px] p-8 border-none flex flex-col">
            <div className="h-1.5 w-12 bg-gray-200 rounded-full mx-auto -mt-4 mb-6" />
            
            {user ? (
              <div className="space-y-6 text-center">
                <div className="w-20 h-20 bg-pink-50 rounded-full flex items-center justify-center mx-auto mb-2">
                  <User className="h-10 w-10 text-pink-200" />
                </div>
                <div>
                  <h3 className="text-xl font-serif font-bold text-gray-900">Olá!</h3>
                  <p className="text-sm text-gray-500">{user.user_metadata?.phone || user.email?.split('@')[0]}</p>
                </div>
                
                <div className="grid gap-3 pt-4">

                  <Button variant="ghost" onClick={handleLogout} className="text-red-500 font-bold">
                    Sair da conta
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                <SheetHeader>
                  <SheetTitle className="text-2xl font-serif text-gray-900">
                    {isRegistering ? "Criar Conta" : "Entrar no Perfil"}
                  </SheetTitle>
                  <p className="text-sm text-gray-500">
                    Acompanhe seus pedidos e salve seus favoritos.
                  </p>
                </SheetHeader>

                <form onSubmit={handleAuth} className="space-y-4">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-gray-400 uppercase tracking-wider">WhatsApp</Label>
                    <Input 
                      placeholder="Ex: 11999999999" 
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      className="h-12 rounded-2xl bg-gray-50 border-none"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-bold text-gray-400 uppercase tracking-wider">Senha</Label>
                    <Input 
                      type="password"
                      placeholder="Sua senha secreta" 
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      className="h-12 rounded-2xl bg-gray-50 border-none"
                    />
                  </div>
                  
                  <Button 
                    type="submit" 
                    disabled={authLoading}
                    className="w-full h-14 rounded-2xl bg-pink-500 hover:bg-pink-600 font-extrabold text-white mt-4"
                  >
                    {authLoading ? "Processando..." : (isRegistering ? "Cadastrar Agora" : "Entrar")}
                  </Button>

                  <button 
                    type="button"
                    onClick={() => setIsRegistering(!isRegistering)}
                    className="w-full text-center text-xs font-bold text-pink-500"
                  >
                    {isRegistering ? "Já tem conta? Entrar" : "Não tem conta? Criar uma agora"}
                  </button>
                </form>
              </div>
            )}
          </SheetContent>
         </Sheet>
      </div>
    </nav>
  );
}
